import axios from "axios";
import { JML_HAS_ACCEPTED_TERMS_KEY } from "./publicUtils";

/**
 * Shared access-token refresh used by both the REST layer (api.ts) and the socket layer
 * (WebSocketInstance.ts).
 *
 * Access tokens are short-lived, so a refresh can be triggered from either layer at any moment.
 * Both go through the single in-flight promise here so a burst of 401s plus a socket
 * `token_expired` event results in exactly one /refresh-token call.
 */

/**
 * Why a refresh finished the way it did.
 *
 * `rejected` and `unavailable` must not be conflated: rejected means the stored credentials will
 * never work again, so the session is over, while unavailable means the backend could not be
 * reached and the same credentials may well work on the next attempt. Treating unavailable as a
 * dead session turns a passing network blip into a forced re-login.
 */
export type TokenRefreshOutcome =
  | { status: "refreshed"; token: string }
  | { status: "rejected" }
  | { status: "unavailable" };

// In-flight refresh shared across all callers.
let refreshPromise: Promise<TokenRefreshOutcome> | null = null;

// Set once a forced logout starts, so the REST and socket layers — which usually fail together
// when a token lapses — cannot each trigger their own logout and navigation.
let isLoggingOut = false;

// Set once the disconnect notice is raised, so the REST and socket layers (which usually fail
// together) raise it once rather than each announcing the same dead session.
let isSessionDisconnectedNotified = false;

type SessionDisconnectedListener = () => void;
const sessionDisconnectedListeners: SessionDisconnectedListener[] = [];

/**
 * True once the session is known to be dead and the re-login prompt is up.
 *
 * Route guards need this: a disconnected session intentionally keeps its localStorage until the
 * user acknowledges, and a stored-but-dead token otherwise reads as "logged in" to the /login guard
 * and "socket down" to the authenticated guard — the two then redirect at each other forever.
 */
export const isSessionDisconnected = (): boolean => isSessionDisconnectedNotified;

/**
 * Subscribe to "the session cannot be renewed" notices, raised when the refresh token itself is
 * expired or invalid. The app shell uses this to show the re-login prompt.
 *
 * A listener registered after the notice has already fired is invoked immediately: the notice can
 * be raised before the app shell mounts its subscription (a missing refresh token fails without a
 * network round trip), and a missed notice leaves the route guard holding the screen with no prompt
 * on it.
 */
export const onSessionDisconnected = (listener: SessionDisconnectedListener): (() => void) => {
  sessionDisconnectedListeners.push(listener);
  if (isSessionDisconnectedNotified) {
    try {
      listener();
    } catch (e) {
      console.error("Session disconnected listener failed", e);
    }
  }
  return () => {
    const index = sessionDisconnectedListeners.indexOf(listener);
    if (index >= 0) sessionDisconnectedListeners.splice(index, 1);
  };
};

/** Every localStorage key that belongs to an authenticated user session. */
const USER_SESSION_KEYS = [
  "JMLAuthToken",
  "JMLRefreshToken",
  "JMLFirstname",
  "JMLLastname",
  "JMLEmail",
  "userId",
  "authProvider",
  "SESSION_TYPE",
  JML_HAS_ACCEPTED_TERMS_KEY,
] as const;

/**
 * Clear all stored user-session state.
 *
 * Notably this removes JMLRefreshToken: leaving a dead refresh token behind would have every
 * later refresh attempt retry a credential that can never succeed.
 */
export const clearUserSession = (): void => {
  USER_SESSION_KEYS.forEach((key) => localStorage.removeItem(key));
};

/** Clear the anonymous public-viewing token. */
export const clearPublicSession = (): void => {
  localStorage.removeItem("JMLPublicToken");
  localStorage.removeItem("JMLPublicTokenExpiry");
};

/**
 * Announce that the session is disconnected and cannot be recovered.
 *
 * Session state is intentionally left in place: the user is told first and the actual logout runs
 * only when they acknowledge, so work on screen is not cleared out from under them. A public
 * (unauthenticated) session has nothing to announce, so its token is just dropped.
 */
export const notifySessionDisconnected = (): void => {
  if (isSessionDisconnectedNotified || isLoggingOut) return;

  if (!localStorage.getItem("JMLAuthToken") && !localStorage.getItem("JMLRefreshToken")) {
    clearPublicSession();
    return;
  }

  isSessionDisconnectedNotified = true;
  sessionDisconnectedListeners.forEach((listener) => {
    try {
      listener();
    } catch (e) {
      console.error("Session disconnected listener failed", e);
    }
  });
};

/**
 * End the session and send the user to the login page.
 *
 * Invoked when the user acknowledges the disconnect prompt, and directly on paths where there is
 * nothing to prompt about. For a public (unauthenticated) session there is nothing to log out of,
 * so the public token is simply dropped and the user stays where they are.
 *
 * Pass `hardReload` to reload the document onto /login instead of navigating in-app. A dead session
 * leaves stale state across the tree (sockets, cached projects, component state), and a fresh
 * document is the only way to guarantee the login page starts clean.
 *
 * Pass `teardown` to run the app shell's own logout routine — the RUM logout signal, the backend
 * /api/logout call, the cross-tab logout broadcast, sessionStorage.clear() and the socket shutdown.
 * This module only owns stored credentials, so without it those steps are skipped: other tabs stay
 * rendered as authenticated and per-user sessionStorage survives into the next login.
 *
 * Returns true when a user session was actually ended, so callers can tell an ended session apart
 * from a no-op (public session, or a logout already in flight).
 */
export const forceLogout = (options: { hardReload?: boolean; teardown?: () => void } = {}): boolean => {
  if (isLoggingOut) return false;

  if (!localStorage.getItem("JMLAuthToken") && !localStorage.getItem("JMLRefreshToken")) {
    clearPublicSession();
    return false;
  }

  isLoggingOut = true;
  isSessionDisconnectedNotified = false;

  // Before the credentials go: the teardown reads session state (and closes the socket that still
  // holds it), so clearing storage first would leave it working from an already-empty session.
  if (options.teardown) {
    try {
      options.teardown();
    } catch (e) {
      console.error("Logout teardown failed", e);
    }
  }

  clearUserSession();
  clearPublicSession();

  // Exactly one navigation on this path. The in-app event and the fallback timer below are
  // deliberately skipped: a second navigation firing while the document load is in flight aborts
  // it and leaves a blank page. `replace` also keeps the dead screen out of history.
  // isLoggingOut stays set on purpose — this document is being torn down.
  //
  // A teardown that navigates on its own (the app shell's logout redirects to /login) is not a
  // problem: it ran in this same synchronous block, so no load has started yet and this replace
  // supersedes it. Requests it left in flight (its fire-and-forget /api/logout) are aborted by the
  // navigation, which is accepted — this path runs when the session is already dead server-side,
  // so there is no server state left to release.
  if (options.hardReload) {
    window.location.replace("/login");
    return true;
  }

  // Prefer in-app navigation via the listener installed by the app shell.
  window.dispatchEvent(new Event("unauthorized"));

  // Fall back to a hard redirect when nothing handled the event (e.g. the listener is not
  // mounted on the current route), so the user is never left on a dead authenticated screen.
  setTimeout(() => {
    if (!window.location.pathname.startsWith("/login")) {
      window.location.href = "/login";
    }
    isLoggingOut = false;
  }, 100);

  return true;
};

type TokenRefreshListener = (newToken: string) => void;
const listeners: TokenRefreshListener[] = [];

/**
 * Subscribe to successful token refreshes. Used by the socket layer to reconnect with the new
 * token, since a socket authenticates once at connect time and cannot pick up a token in place.
 *
 * Returns an unsubscribe function. Callers with a lifetime shorter than the page MUST call it:
 * a retained listener from a discarded object keeps acting on every later refresh.
 */
export const onTokenRefreshed = (listener: TokenRefreshListener): (() => void) => {
  listeners.push(listener);
  return () => {
    const index = listeners.indexOf(listener);
    if (index >= 0) listeners.splice(index, 1);
  };
};

/** Decode a JWT's `exp` claim as epoch milliseconds. Returns null when absent or unparseable. */
export const getTokenExpiryMs = (token: string | null): number | null => {
  if (!token) return null;
  const parts = token.split(".");
  if (parts.length < 2) return null;
  try {
    const payload = JSON.parse(atob(parts[1]));
    return typeof payload.exp === "number" ? payload.exp * 1000 : null;
  } catch (e) {
    console.error("Failed to decode token expiry", e);
    return null;
  }
};

/**
 * True when the stored access token has a readable expiry that is already past or within `skewMs`
 * of it. The skew avoids sending a token that will expire in flight.
 *
 * False when no token is stored, or its expiry cannot be read (no `exp` claim, unparseable
 * payload): there is nothing to pre-empt, so those cases are left to the 401 handler.
 */
export const isAuthTokenExpiring = (skewMs: number = 5000): boolean => {
  const expiryMs = getTokenExpiryMs(localStorage.getItem("JMLAuthToken"));
  if (expiryMs === null) return false;
  return Date.now() > expiryMs - skewMs;
};

const performTokenRefresh = async (
  email: string,
  refreshToken: string,
): Promise<TokenRefreshOutcome> => {
  try {
    // Plain axios, not the `api` instance: this call must not pass through the auth
    // interceptors it exists to serve.
    const res = await axios.post(
      `${process.env.REACT_APP_SIMPLIFAI_BACKEND_API_URL}/refresh-token`,
      { email, refresh_token: refreshToken },
    );
    if (!res.data.success) {
      console.error("Token refresh response indicated failure", res.data);
      return { status: "rejected" };
    }

    const newToken: string = res.data.token;
    localStorage.setItem("JMLAuthToken", newToken);
    if (res.data.refresh_token) {
      localStorage.setItem("JMLRefreshToken", res.data.refresh_token);
    }
    if (localStorage.getItem("JMLPublicToken")) {
      localStorage.removeItem("JMLPublicToken");
    }

    listeners.forEach((listener) => {
      try {
        listener(newToken);
      } catch (e) {
        console.error("Token refresh listener failed", e);
      }
    });

    return { status: "refreshed", token: newToken };
  } catch (refreshError) {
    console.error("Token refresh request failed", refreshError);
    // A 4xx means the backend looked at these credentials and turned them down; anything else
    // (network error, timeout, 5xx) says nothing about the credentials themselves.
    const responseStatus = axios.isAxiosError(refreshError) ? refreshError.response?.status : undefined;
    const isCredentialRejection =
      responseStatus !== undefined && responseStatus >= 400 && responseStatus < 500;
    return { status: isCredentialRejection ? "rejected" : "unavailable" };
  }
};

/**
 * Refresh the access token, reusing any refresh already in flight.
 *
 * Callers that need to tell "the session is over" apart from "the backend is unreachable" should
 * use this; `refreshAuthToken` collapses both into null.
 */
export const refreshAuthTokenWithOutcome = async (): Promise<TokenRefreshOutcome> => {
  const refreshToken = localStorage.getItem("JMLRefreshToken");
  const email = localStorage.getItem("JMLEmail");
  // Nothing to refresh with. Reported as rejected rather than unavailable: no attempt can ever
  // succeed from here, which is the same dead end as a refused credential.
  if (!refreshToken || !email) {
    return { status: "rejected" };
  }

  if (!refreshPromise) {
    refreshPromise = performTokenRefresh(email, refreshToken).finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
};

/**
 * Refresh the access token, reusing any refresh already in flight.
 * Returns the new token, or null when no credentials are stored or the refresh failed.
 */
export const refreshAuthToken = async (): Promise<string | null> => {
  const outcome = await refreshAuthTokenWithOutcome();
  return outcome.status === "refreshed" ? outcome.token : null;
};
